import { z } from "zod";

/** Implicit TLS or STARTTLS with certificate verification; deliberately no plaintext option. */
export const imapSecurity = z.enum(["tls", "starttls"]);
export type ImapSecurity = z.infer<typeof imapSecurity>;

export type ImapTransport = "imap" | "smtp";

/** Providers whose mailboxes connect over OAuth instead of this connector. */
export type ImapOauthProvider = "google" | "microsoft";

/**
 * The presets with server settings of their own, each of which is a catalog tile.
 *
 * Listed before {@link IMAP_PRESET_IDS} and spread into it, so adding a preset
 * here gives it a tile and a valid `presetId` at once.
 */
export const IMAP_PRESET_TILE_IDS = [
  "verizon",
  "att",
  "xfinity",
  "yahoo",
  "aol",
  "icloud",
] as const;
export const imapPresetTileId = z.enum(IMAP_PRESET_TILE_IDS);
/** A preset a tile can name. Excludes `custom`, which is the absence of a preset. */
export type ImapPresetTileId = z.infer<typeof imapPresetTileId>;

/** Every value a connector's stored `presetId` can hold; `custom` means the servers came from Advanced. */
export const IMAP_PRESET_IDS = [...IMAP_PRESET_TILE_IDS, "custom"] as const;
export const imapPresetId = z.enum(IMAP_PRESET_IDS);
export type ImapPresetId = z.infer<typeof imapPresetId>;

export const imapEndpoint = z.object({
  host: z.string(),
  port: z.number().int(),
  security: imapSecurity,
});
export type ImapEndpoint = z.infer<typeof imapEndpoint>;

/** Anything else is refused: an unconstrained port would make the connect endpoint a port scanner. */
export const MAIL_PORTS: readonly number[] = [143, 993, 465, 587];

/**
 * At least two labels: a bare word (`localhost`) is refused here, a literal IP
 * by the host guard, which needs a name to verify the certificate against.
 */
const HOSTNAME_RE = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/;

const mailHost = z
  .string()
  .trim()
  .toLowerCase()
  .max(253)
  .regex(HOSTNAME_RE, "Enter a server hostname such as imap.example.com");

const mailPort = z
  .number()
  .int()
  .refine((port) => MAIL_PORTS.includes(port), {
    message: "Use a standard mail port: 143, 993, 465, or 587",
  });

/** Every field optional so a user on a known provider can fix one drifted port alone. */
const imapEndpointOverride = z.object({
  host: mailHost.optional(),
  port: mailPort.optional(),
  security: imapSecurity.optional(),
});

/** Case is kept: identity lowercases the address, but a custom server may take a case-sensitive login. */
const emailAddress = z.string().trim().email().max(254);

export const connectImapReq = z
  .object({
    email: emailAddress,
    password: z.string().min(1).max(1024),
    /**
     * The tile the user came from, asserted against the address's own domain.
     *
     * It never supplies the servers — the domain still resolves those, so there
     * is one answer for a given address. Sending it says "I expect this to be a
     * Yahoo mailbox", and the server refuses when it is not, rather than
     * attempting a Yahoo login for an AT&T address and blaming the password.
     */
    presetId: imapPresetTileId.optional(),
    advanced: z
      .object({
        imap: imapEndpointOverride.optional(),
        smtp: imapEndpointOverride.optional(),
      })
      .optional(),
  })
  // A preset tile shows no Advanced section; accepting overrides from one anyway
  // would give the same address two sources of servers.
  .refine((req) => !(req.presetId && req.advanced), {
    message: "A preset connector takes its servers from the preset, not from overrides",
    path: ["advanced"],
  });
export type ConnectImapReq = z.infer<typeof connectImapReq>;

export type ConnectImapResp = {
  connectorId: number;
  email: string;
  presetId: ImapPresetId;
};

export const connectImapErrorCode = z.enum([
  "not_available",
  "rate_limited",
  "invalid_email",
  "oauth_provider",
  "unknown_provider",
  "preset_mismatch",
  "blocked_host",
  "bad_password",
  "unreachable",
  "server_busy",
  "owned_by_other_account",
  "already_connected",
]);
export type ConnectImapErrorCode = z.infer<typeof connectImapErrorCode>;

export type ConnectImapErrorResp = {
  error: string;
  code: ConnectImapErrorCode;
  /** Set with `oauth_provider`: the connector that should own this address instead. */
  oauthProvider?: ImapOauthProvider;
  /**
   * Set with `preset_mismatch`: the tile that does serve this address, when one
   * does. Absent for a domain no preset covers, which belongs to the fallback tile.
   */
  expectedPresetId?: ImapPresetTileId;
};

export const imapPresetLookupQuery = z.object({
  email: emailAddress,
});

/** Shared so the account row's Reconnect reaches the endpoint without a catalog item in hand. */
export const IMAP_CONNECT_PATH = "/api/a/connectors/imap/connect";
export const IMAP_PRESETS_PATH = "/api/a/connectors/imap/presets";

/** What the connect form learns about an address before asking for a password. */
export type ImapPresetLookupResp =
  | {
      kind: "preset";
      presetId: Exclude<ImapPresetId, "custom">;
      name: string;
      passwordHelp: string;
      imap: ImapEndpoint;
      smtp: ImapEndpoint;
    }
  | { kind: "oauth_provider"; provider: ImapOauthProvider }
  | { kind: "unknown_provider" }
  | { kind: "invalid_email" };
